import type { PreComputedFilterOfferIds } from '@cc-shopping/contracts/credit-cards-page/roundup/types';
import { isEqual } from '@cc-shopping/utils/object/isEqual';
import { createStore } from 'zustand';
import { FilterType } from './enums';
import type { FilterState, OffersFilterStoreState, UpdateParam } from './types';
import {
  cleanupFiltersAndGetAvailableOffersCount,
  DEFAULT_CARD_FEATURES,
  DEFAULT_SORT_BY,
  getNonDefaultSelectedFilters,
} from './utils';

export const initialFilterState: FilterState = {
  cardFeatureFilter: DEFAULT_CARD_FEATURES,
  issuerFilter: [],
  creditRangeFilter: [],
  sortBy: DEFAULT_SORT_BY,
};

const initialState = {
  isLoading: false,
  appliedFilters: initialFilterState,
  filterFormState: initialFilterState,
  selectedNonDefaultFilters: [],
};

export function createOffersFilterStore({
  areFiltersEnabled,
  preComputedFilterOfferIds,
}: {
  areFiltersEnabled: boolean;
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
}) {
  const { availableOffersCount } = cleanupFiltersAndGetAvailableOffersCount({
    preComputedFilterOfferIds,
    newSelectedFilters: initialFilterState,
  });
  return createStore<OffersFilterStoreState>()((set, get) => ({
    areFiltersEnabled,
    preComputedFilterOfferIds,
    availableOffersCount,
    ...initialState,

    handleLoading: () => {
      set({ isLoading: true });
      setTimeout(() => {
        set({ isLoading: false });
      }, 100);
    },

    handleSingleFormUpdate: (update: UpdateParam) => {
      const { filterFormState: previouslySelectedFilters } = get();

      const newSelectedFilters = {
        ...previouslySelectedFilters,
        [update.type]: update.value,
      };
      if (update.type !== FilterType.SORT_BY) {
        const { cleanedFilters, availableOffersCount } = cleanupFiltersAndGetAvailableOffersCount({
          preComputedFilterOfferIds,
          newSelectedFilters,
        });

        const selectedNonDefaultFilters = getNonDefaultSelectedFilters({
          newSelectedFilters: cleanedFilters,
          issuersIdNameMap: preComputedFilterOfferIds.issuersIdNameMap,
        });

        set({
          availableOffersCount,
          filterFormState: cleanedFilters,
          selectedNonDefaultFilters,
        });
      } else {
        const selectedNonDefaultFilters = getNonDefaultSelectedFilters({
          newSelectedFilters,
          issuersIdNameMap: preComputedFilterOfferIds.issuersIdNameMap,
        });

        set({
          filterFormState: newSelectedFilters,
          selectedNonDefaultFilters,
        });
      }
    },

    handleClearFilters: () => {
      set({
        availableOffersCount,
        filterFormState: initialState.filterFormState,
        selectedNonDefaultFilters: initialState.selectedNonDefaultFilters,
      });
    },

    handleApplyFilters: () => {
      const {
        appliedFilters: previouslyAppliedFilters,
        filterFormState: selectedFilters,
        handleLoading,
      } = get();
      if (isEqual(previouslyAppliedFilters, selectedFilters)) {
        return;
      }
      handleLoading();
      set({ appliedFilters: selectedFilters });
    },
  }));
}
